/**
 * Error Dashboard
 * Displays error statistics, aggregations, and alerts
 */

import { useEffect, useState } from 'react'
import { useOrganizationId } from '../hooks/useOrganizationId'
import Card from '../components/Card'
import Button from '../components/Button'
import {
  getErrorStatistics,
  getUnacknowledgedAlerts,
  acknowledgeAlert,
  flushAggregations,
} from '../services/errorTracking'
import { getAggregatedErrors } from '../services/errorAggregation'
import { getAlertHistory } from '../services/errorAlerting'
import {
  AlertTriangle,
  TrendingUp,
  Activity,
  Bell,
  RefreshCw,
  CheckCircle2,
  XCircle,
  Info,
} from 'lucide-react'

const ErrorDashboard = () => {
  const organizationId = useOrganizationId()
  const [statistics, setStatistics] = useState(null)
  const [alerts, setAlerts] = useState([])
  const [aggregatedErrors, setAggregatedErrors] = useState([])
  const [alertHistory, setAlertHistory] = useState([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)

  useEffect(() => {
    if (organizationId) {
      loadData()
    }
  }, [organizationId])

  const loadData = async () => {
    setLoading(true)
    try {
      const [stats, unackAlerts, aggregated, history] = await Promise.all([
        getErrorStatistics(organizationId, 24),
        getUnacknowledgedAlerts(organizationId),
        Promise.resolve(getAggregatedErrors(20)),
        Promise.resolve(getAlertHistory(20)),
      ])

      setStatistics(stats)
      setAlerts(unackAlerts)
      setAggregatedErrors(aggregated)
      setAlertHistory(history)
    } catch (error) {
      console.error('Failed to load error data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleRefresh = async () => {
    setRefreshing(true)
    try {
      // Flush aggregations first
      if (organizationId) {
        const userId = (await import('../services/supabase')).supabase.auth.getUser().then(({ data }) => data?.user?.id)
        await flushAggregations(organizationId, await userId)
      }
      await loadData()
    } catch (error) {
      console.error('Failed to refresh:', error)
    } finally {
      setRefreshing(false)
    }
  }

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      const { supabase } = await import('../services/supabase')
      const {
        data: { user },
      } = await supabase.auth.getUser()
      await acknowledgeAlert(alertId, user?.id)
      await loadData()
    } catch (error) {
      console.error('Failed to acknowledge alert:', error)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p>Loading error data...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="section-container">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Error Dashboard</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Monitor and manage platform errors</p>
        </div>
        <Button onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <div className="space-y-6">

      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <div className="flex items-center justify-between p-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Total Errors</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{statistics.total_errors || 0}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-500" />
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between p-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Unique Errors</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{statistics.unique_errors || 0}</p>
              </div>
              <AlertTriangle className="w-8 h-8 text-yellow-500" />
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between p-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Critical Errors</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{statistics.critical_errors || 0}</p>
              </div>
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
          </Card>

          <Card>
            <div className="flex items-center justify-between p-4">
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Error Rate</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                  {statistics.error_rate ? statistics.error_rate.toFixed(1) : 0}/hr
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-500" />
            </div>
          </Card>
        </div>
      )}

      {/* Unacknowledged Alerts */}
      {alerts.length > 0 && (
        <Card>
          <div className="flex items-center justify-between mb-4 p-6 pb-4">
            <h2 className="text-xl font-semibold flex items-center text-gray-900 dark:text-white">
              <Bell className="w-5 h-5 mr-2" />
              Unacknowledged Alerts ({alerts.length})
            </h2>
          </div>
          <div className="space-y-2 px-6 pb-6">
            {alerts.map((alert) => (
              <div
                key={alert.id}
                className="flex items-center justify-between p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg"
              >
                <div className="flex-1 pr-4">
                  <p className="font-medium text-gray-900 dark:text-white">{alert.alert_data?.message || 'Alert'}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {alert.alert_type} • {new Date(alert.sent_at).toLocaleString()}
                  </p>
                </div>
                <Button
                  size="sm"
                  onClick={() => handleAcknowledgeAlert(alert.id)}
                  variant="outline"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" />
                  Acknowledge
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Top Errors */}
      {statistics?.top_errors && statistics.top_errors.length > 0 && (
        <Card>
          <h2 className="text-xl font-semibold mb-4 flex items-center p-6 pb-4 text-gray-900 dark:text-white">
            <Activity className="w-5 h-5 mr-2" />
            Top Errors
          </h2>
          <div className="space-y-2 px-6 pb-6">
            {statistics.top_errors.map((error, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
              >
                <div className="flex-1 pr-4">
                  <p className="font-medium text-gray-900 dark:text-white">{error.message}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Count: {error.count} • Last seen: {new Date(error.last_seen).toLocaleString()}
                  </p>
                </div>
                <span
                  className={`px-3 py-1 rounded text-xs font-semibold ${
                    error.severity === 'critical'
                      ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      : error.severity === 'error'
                      ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
                      : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                  }`}
                >
                  {error.severity}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Aggregated Errors */}
      {aggregatedErrors.length > 0 && (
        <Card>
          <h2 className="text-xl font-semibold mb-4 p-6 pb-4 text-gray-900 dark:text-white">Recent Error Aggregations</h2>
          <div className="space-y-2 px-6 pb-6">
            {aggregatedErrors.map((error, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex-1 pr-4">
                  <p className="font-medium text-gray-900 dark:text-white">{error.message}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Count: {error.count} • Component: {error.component || 'N/A'}
                  </p>
                </div>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  {new Date(error.lastSeen).toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Alert History */}
      {alertHistory.length > 0 && (
        <Card>
          <h2 className="text-xl font-semibold mb-4 p-6 pb-4 text-gray-900 dark:text-white">Alert History</h2>
          <div className="space-y-2 px-6 pb-6">
            {alertHistory.map((alert, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                <div className="flex-1 pr-4">
                  <p className="font-medium text-gray-900 dark:text-white">{alert.data?.message || 'Alert'}</p>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {alert.type} • {new Date(alert.timestamp).toLocaleString()}
                  </p>
                </div>
                <Info className="w-4 h-4 text-gray-400 dark:text-gray-500" />
              </div>
            ))}
          </div>
        </Card>
      )}

      {!statistics && alerts.length === 0 && aggregatedErrors.length === 0 && (
        <Card>
          <div className="text-center py-12 px-6">
            <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-400">No errors or alerts to display</p>
          </div>
        </Card>
      )}
      </div>
    </div>
  )
}

export default ErrorDashboard

